import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/site/LegalPage";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Use — ClutchCube" },
      {
        name: "description",
        content:
          "The terms that govern your access to and use of ClutchCube.com, including intellectual property, acceptable use, and disclaimers.",
      },
      { property: "og:title", content: "Terms of Use — ClutchCube" },
      {
        property: "og:description",
        content:
          "The terms that govern your access to and use of ClutchCube.com, including intellectual property, acceptable use, and disclaimers.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://clutchcube.com/terms" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://clutchcube.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Use"
      subtitle="Please read these Terms of Use carefully before accessing or using ClutchCube.com."
      updated="2026"
    >
      <LegalSection title="1. Acceptance of Terms">
        <p>
          By accessing or using ClutchCube.com (the &ldquo;Website&rdquo;), you agree to be bound by these Terms of Use and
          our Privacy Policy. If you do not agree, please do not use the Website.
        </p>
      </LegalSection>

      <LegalSection title="2. Website Purpose">
        <p>
          ClutchCube.com is provided to share information about ClutchCube, its products, planned experiences, franchise
          concept, future business plans, and potential investor opportunities. The Website is informational and does not
          constitute an offer to sell, or a solicitation of an offer to buy, any securities or franchise interests.
        </p>
      </LegalSection>

      <LegalSection title="3. Intellectual Property">
        <p>
          All content on the Website is the property of ClutchCube or its licensors and is protected by U.S. and
          international intellectual property laws. Protected materials include, without limitation:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>The ClutchCube name and brand</li>
          <li>Logos, marks, and visual identity elements</li>
          <li>Images, renderings, and photography</li>
          <li>Videos, animations, and interactive experiences</li>
          <li>The investor pitch deck and related marketing materials</li>
          <li>Copy, text, and other written content</li>
          <li>Trademarks, service marks, and trade dress</li>
          <li>Copyrights and other proprietary rights</li>
        </ul>
        <p>
          You may not copy, modify, distribute, publish, sell, or otherwise exploit any content from the Website without
          our prior written consent.
        </p>
      </LegalSection>

      <LegalSection title="4. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Use the Website for any unlawful, harmful, or fraudulent purpose</li>
          <li>Interfere with or disrupt the Website or its infrastructure</li>
          <li>Attempt unauthorized access to any portion of the Website or related systems</li>
          <li>Use automated tools to scrape, harvest, or extract content</li>
          <li>Impersonate any person or misrepresent your affiliation</li>
          <li>Introduce malware, viruses, or other harmful code</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. User Submissions">
        <p>
          Information you voluntarily submit through forms or communications should not be considered confidential unless
          expressly agreed in writing. By submitting information, you grant ClutchCube a non-exclusive, royalty-free license
          to use it in connection with responding to your inquiry, communicating with you, and operating our business. Do
          not submit information you do not have the right to share.
        </p>
      </LegalSection>

      <LegalSection title="6. No Guarantees">
        <p>
          The Website and its content are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; ClutchCube makes no
          warranties, express or implied, regarding accuracy, completeness, reliability, availability, or fitness for a
          particular purpose. Forward-looking statements, projections, renderings, and business plans reflect current
          expectations and are subject to change without notice.
        </p>
      </LegalSection>

      <LegalSection title="7. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, ClutchCube and its affiliates, officers, employees, and agents shall not
          be liable for any indirect, incidental, consequential, special, or punitive damages arising from or related to
          your use of the Website, even if advised of the possibility of such damages.
        </p>
      </LegalSection>

      <LegalSection title="8. Third-Party Links">
        <p>
          The Website may contain links to third-party sites or resources. We do not control and are not responsible for
          the content, policies, or practices of any third-party site. Accessing third-party links is at your own risk.
        </p>
      </LegalSection>

      <LegalSection title="9. Governing Law">
        <p>
          These Terms of Use are governed by the laws of the United States and the state in which ClutchCube is
          headquartered, without regard to conflict-of-laws principles. Any disputes will be resolved in the courts of
          competent jurisdiction in that state.
        </p>
      </LegalSection>

      <LegalSection title="10. Changes to These Terms">
        <p>
          We may revise these Terms of Use at any time. Updated terms will be posted on this page with a new effective
          date. Your continued use of the Website constitutes acceptance of the revised terms.
        </p>
      </LegalSection>

      <LegalSection title="11. Ownership and Operations">
        <p>
          ClutchCube is a venture operated by Colowebo LLC and supported through the VentureX OS operating framework.
          VentureX OS provides operational infrastructure, automation systems, analytics, and administrative support for
          ventures within the VentureX ecosystem.
        </p>
      </LegalSection>

      <LegalSection title="12. Contact Us">
        <p>
          Questions about these Terms of Use? Please reach out through the{" "}
          <a href="/#invest" className="text-primary hover:underline">
            investor contact form
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
